export type AttendanceParseKind = 'plain' | 'daily-records' | 'table' | 'list-records' | 'stats'

export type AttendanceParsedContent = {
  kind: AttendanceParseKind
  summary: string
  columns: string[]
  rows: Record<string, string>[]
  confidence: number
}

const MIN_CONFIDENCE = 0.55
const MIN_ROWS = 1

const STATUS_VALUES =
  'Present|Absent|Late|Leave|Bunk|Unmark|Checkout|Early\\s*Checkout|Holiday|Half\\s*Day|Excused|On\\s*Leave|Tardy|Not\\s*Present|Half\\s*day'

const STATUS_PATTERN = new RegExp(`^(${STATUS_VALUES})$`, 'i')

const DAILY_LINE = new RegExp(`^(.+?)\\s*[-–—:]\\s*(${STATUS_VALUES})\\s*$`, 'i')

const STATS_LINE = /^(.{2,80}?)\s*:\s*(.+)$/

function normalizeStatus(status: string): string {
  const trimmed = status.trim()
  if (/^half\s*day$/i.test(trimmed)) return 'Half Day'
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase()
}

const MONTHS =
  'January|February|March|April|May|June|July|August|September|October|November|December'

const DATE_HEADING = new RegExp(`^(${MONTHS})\\s+\\d{1,2},\\s+\\d{4}$`, 'i')

const STATUS_LIST_BULLET = new RegExp(`^[-*•]\\s*(${STATUS_VALUES})\\s*:\\s*(.*)$`, 'i')

function splitNameList(raw: string): string[] {
  return (raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function tryGroupedListRecords(text: string): AttendanceParsedContent | null {
  const lines = text.split(/\r?\n/)

  const rows: Record<string, string>[] = []
  const summaryLines: string[] = []

  let currentDate: string | null = null
  let collectingStatus: string | null = null
  let collectingNames = ''
  let started = false

  const flushCollecting = () => {
    if (!currentDate || !collectingStatus) return
    const names = splitNameList(collectingNames)
    const status = normalizeStatus(collectingStatus)
    for (const name of names) {
      rows.push({Date: currentDate, Status: status, Name: name})
    }
  }

  for (let idx = 0; idx < lines.length; idx += 1) {
    const rawLine = lines[idx]
    const line = rawLine.trim()

    if (!line) {
      if (!started) summaryLines.push(rawLine)
      continue
    }

    // Date heading line (e.g. "September 23, 2025")
    if (DATE_HEADING.test(line)) {
      started = true
      flushCollecting()
      collectingStatus = null
      collectingNames = ''
      currentDate = line
      continue
    }

    // Bullet status line (e.g. "- Present: A, B, C")
    const bullet = line.match(STATUS_LIST_BULLET)
    if (bullet && currentDate) {
      started = true
      flushCollecting()
      collectingStatus = bullet[1]
      collectingNames = bullet[2] ?? ''
      continue
    }

    // Continuation line for a long name list (LLMs often wrap)
    if (currentDate && collectingStatus && !/^[-*•]\s*/.test(line) && !DATE_HEADING.test(line)) {
      collectingNames = `${collectingNames} ${line}`
      continue
    }

    // Non-structured content: keep only before first date as "summary"
    if (!started) summaryLines.push(rawLine)
  }

  flushCollecting()

  if (rows.length < MIN_ROWS) return null

  const nonEmpty = lines.map((l) => l.trim()).filter(Boolean).length || 1
  const matchRatio = Math.min(1, rows.length / nonEmpty)
  const confidence = Math.min(1, 0.7 + matchRatio * 0.25)

  return {
    kind: 'list-records',
    summary: summaryLines.join('\n').trim(),
    columns: ['Date', 'Status', 'Name'],
    rows,
    confidence,
  }
}

function isTableRow(line: string): boolean {
  const trimmed = line.trim()
  return trimmed.startsWith('|') && trimmed.endsWith('|')
}

function isTableSeparator(line: string): boolean {
  const trimmed = line.trim()
  if (!isTableRow(trimmed)) return false
  return /^\|[\s:|\\-]+\|$/.test(trimmed)
}

function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) =>
      cell
        .trim()
        .replace(/^\*\*(.+)\*\*$/s, '$1')
        .replace(/^__(.+)__$/s, '$1')
        .replace(/^\*(.+)\*$/s, '$1')
        .replace(/^_(.+)_$/s, '$1')
        .replace(/^`(.+)`$/s, '$1')
        .trim()
    )
}

function tryMarkdownTable(text: string): AttendanceParsedContent | null {
  const lines = text.split(/\r?\n/)
  const blocks: {start: number; end: number}[] = []

  let i = 0
  while (i < lines.length) {
    if (!isTableRow(lines[i])) {
      i += 1
      continue
    }
    const start = i
    while (i < lines.length && isTableRow(lines[i])) {
      i += 1
    }
    blocks.push({start, end: i})
  }

  if (blocks.length === 0) return null

  const {start, end} = blocks[blocks.length - 1]
  const tableLines = lines.slice(start, end).map((l) => l.trim())
  if (tableLines.length < 2) return null

  const headers = splitTableRow(tableLines[0])
  if (headers.length < 2 || headers.every((h) => !h)) return null

  const dataStart = tableLines.length > 1 && isTableSeparator(tableLines[1]) ? 2 : 1

  const rows: Record<string, string>[] = []
  for (let r = dataStart; r < tableLines.length; r += 1) {
    if (isTableSeparator(tableLines[r])) continue
    const cells = splitTableRow(tableLines[r])
    if (cells.length < 2) continue
    const row: Record<string, string> = {}
    headers.forEach((header, idx) => {
      const key = header || `Column ${idx + 1}`
      row[key] = cells[idx] ?? ''
    })
    rows.push(row)
  }

  if (rows.length < MIN_ROWS) return null

  const before = lines.slice(0, start).join('\n').trim()
  const after = lines.slice(end).join('\n').trim()
  const summary = [before, after].filter(Boolean).join('\n\n')

  return {
    kind: 'table',
    summary,
    columns: headers,
    rows,
    confidence: 0.9,
  }
}

function tryDailyRecords(text: string): AttendanceParsedContent | null {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
  const rows: Record<string, string>[] = []
  const summaryLines: string[] = []

  for (const line of lines) {
    const direct = line.match(DAILY_LINE)
    if (direct) {
      rows.push({
        Date: direct[1].trim(),
        Status: normalizeStatus(direct[2]),
      })
      continue
    }

    const bullet = line.match(/^[-*•]\s+(.+)$/)
    if (bullet) {
      const inner = bullet[1].trim()
      const fromBullet = inner.match(DAILY_LINE)
      if (fromBullet) {
        rows.push({
          Date: fromBullet[1].trim(),
          Status: normalizeStatus(fromBullet[2]),
        })
        continue
      }
      const colonParts = inner.match(/^(.+?)\s*:\s*(.+)$/)
      if (colonParts && STATUS_PATTERN.test(colonParts[2].trim())) {
        rows.push({
          Date: colonParts[1].trim(),
          Status: normalizeStatus(colonParts[2]),
        })
        continue
      }
    }

    const numbered = line.match(/^\d+[.)]\s+(.+)$/)
    if (numbered) {
      const inner = numbered[1].trim()
      const fromNumber = inner.match(DAILY_LINE)
      if (fromNumber) {
        rows.push({
          Date: fromNumber[1].trim(),
          Status: normalizeStatus(fromNumber[2]),
        })
        continue
      }
    }

    summaryLines.push(line)
  }

  if (rows.length < MIN_ROWS) return null

  const matchRatio = rows.length / lines.length
  if (lines.length > 4 && matchRatio < 0.25) return null

  const confidence = Math.min(1, 0.6 + matchRatio * 0.35 + Math.min(rows.length, 5) * 0.03)

  return {
    kind: 'daily-records',
    summary: summaryLines.join('\n\n'),
    columns: ['Date', 'Status'],
    rows,
    confidence,
  }
}

function tryStats(text: string): AttendanceParsedContent | null {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
  const rows: Record<string, string>[] = []
  const summaryLines: string[] = []

  for (const line of lines) {
    const m = line.match(STATS_LINE)
    if (m && !DAILY_LINE.test(line)) {
      const metric = m[1].trim()
      const value = m[2].trim()
      if (/^\d+(\.\d+)?%?$/.test(value) || /\d+\s*(day|days|absent|present)/i.test(value)) {
        rows.push({Metric: metric, Value: value})
        continue
      }
    }
    summaryLines.push(line)
  }

  if (rows.length < 2) return null

  const matchRatio = rows.length / lines.length
  if (matchRatio < 0.4) return null

  return {
    kind: 'stats',
    summary: summaryLines.join('\n\n'),
    columns: ['Metric', 'Value'],
    rows,
    confidence: 0.65 + matchRatio * 0.25,
  }
}

const PARSERS: Array<(text: string) => AttendanceParsedContent | null> = [
  tryMarkdownTable,
  tryGroupedListRecords,
  tryDailyRecords,
  tryStats,
]

export function parseAttendanceAnswer(text: string): AttendanceParsedContent {
  const raw = (text || '').trim()
  if (!raw) {
    return {kind: 'plain', summary: '', columns: [], rows: [], confidence: 1}
  }

  for (const parse of PARSERS) {
    const result = parse(raw)
    if (result && result.confidence >= MIN_CONFIDENCE && result.rows.length >= MIN_ROWS) {
      return result
    }
  }

  return {kind: 'plain', summary: raw, columns: [], rows: [], confidence: 1}
}

export function hasAttendanceTable(content?: AttendanceParsedContent): boolean {
  return Boolean(content && content.kind !== 'plain' && content.rows.length > 0)
}
